import * as React from 'react'
import {
  AppBar, Avatar, Box, Divider, Drawer,
  IconButton, ListItemIcon, Menu, MenuItem,
  Toolbar, Typography
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LogoutIcon from '@mui/icons-material/Logout'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import HomeIcon from '@mui/icons-material/Home'
import StorefrontIcon from '@mui/icons-material/Storefront'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RushmyFoodLogo from '../../assets/img/rushmyfood-logo.png'
import { useLocation, useHistory } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import SignOutAllDevicesDialog from '../Security/SignOutAllDevicesDialog'

const drawerWidth = 240

const NAV_LINKS = [
  { label: 'Home', icon: HomeIcon, href: '#/restaurant/home' },
  { label: 'Restaurants', icon: StorefrontIcon, href: '#/restaurant/list' },
  { label: 'Orders', icon: ReceiptLongIcon, href: '#/restaurant/orders' },
]

function MerchantSidebar(props) {
  const location = useLocation()
  const history = useHistory()
  const client = useApolloClient()
  const { window } = props
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [signOutAll, setSignOutAll] = React.useState(false)
  const [userInfo, setUserInfo] = React.useState({ name: 'User', email: '' })
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(false)

  React.useEffect(() => {
    const data = localStorage.getItem('user-enatega')
    if (data) {
      const p = JSON.parse(data)
      setUserInfo({ name: p.name || (p.email ? p.email.split('@')[0] : 'User'), email: p.email || '' })
      setIsSuperAdmin(p.userType === 'ADMIN' || p.userType === 'SUPER_ADMIN')
    }
  }, [])

  const handleLogout = () => {
    setAnchorEl(null)
    localStorage.removeItem('user-enatega')
    localStorage.removeItem('restaurant_id')
    client.clearStore()
    history.push('/auth/login')
  }

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen)
  const container = window !== undefined ? () => window().document.body : undefined

  const isActive = href => {
    const path = href.replace('#', '')
    return location.pathname === path
  }

  const NavLink = ({ label, icon: Icon, href }) => {
    const active = isActive(href)
    return (
      <Box
        component="a"
        href={href}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 1.5, py: 0.9, mb: 0.25,
          borderRadius: '10px', textDecoration: 'none', cursor: 'pointer',
          bgcolor: active ? 'rgba(59,130,246,0.15)' : 'transparent',
          borderLeft: active ? '3px solid #3b82f6' : '3px solid transparent',
          transition: 'all 0.15s ease',
          '&:hover': { bgcolor: active ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.06)' }
        }}
      >
        <Box sx={{
          width: 18, height: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          '& svg': {
            width: 18, height: 18,
            color: active ? '#93c5fd' : 'rgba(255,255,255,0.55)'
          }
        }}>
          <Icon />
        </Box>
        <Typography sx={{
          fontSize: '0.83rem',
          fontWeight: active ? 600 : 400,
          color: active ? '#fff' : 'rgba(255,255,255,0.65)',
          lineHeight: 1
        }}>
          {label}
        </Typography>
      </Box>
    )
  }

  const DrawerContent = (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      bgcolor: '#0f172a', overflowY: 'auto', overflowX: 'hidden',
      '&::-webkit-scrollbar': { width: 0 }
    }}>
      {/* Logo + branding */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            bgcolor: '#fff', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)'
          }}>
            <img src={RushmyFoodLogo} alt="RushMyFood" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff', lineHeight: 1.2 }}>
              RushMyFood
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Merchant Panel
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Back to Super Admin button (only for super admins) */}
      {isSuperAdmin && (
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <Box
            component="a"
            href="#/super_admin/vendors"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 0.9, borderRadius: '10px', textDecoration: 'none', cursor: 'pointer',
              bgcolor: 'rgba(249,115,22,0.12)',
              border: '1px solid rgba(249,115,22,0.3)',
              transition: 'all 0.15s ease',
              '&:hover': { bgcolor: 'rgba(249,115,22,0.2)' }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 15, color: '#fb923c' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#fb923c', lineHeight: 1 }}>
              Back to Super Admin
            </Typography>
          </Box>
        </Box>
      )}

      {/* Nav links */}
      <Box sx={{ px: 1.5, py: 1.5, flex: 1 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase', px: 1.5, mb: 0.75 }}>
          MENU
        </Typography>
        {NAV_LINKS.map(link => (
          <NavLink key={link.label} {...link} />
        ))}
      </Box>

      {/* User profile at bottom */}
      <Box sx={{ px: 1.5, pb: 2, flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.07)', pt: 1.5 }}>
        <Box
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1, borderRadius: '10px', cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }
          }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#f97316', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>
            {userInfo.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#fff', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {userInfo.name}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {userInfo.email}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18, flexShrink: 0 }} />
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 160, mb: 1 } }}>
          <MenuItem onClick={() => { setAnchorEl(null); history.push('/restaurant/profile') }} sx={{ py: 1.2, fontSize: '0.85rem' }}>
            <ListItemIcon><PersonOutlineIcon sx={{ color: '#666' }} /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); setSignOutAll(true) }} sx={{ py: 1.2, fontSize: '0.85rem' }}>
            <ListItemIcon><DevicesRoundedIcon sx={{ color: '#666' }} /></ListItemIcon>
            Sign out of all devices
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleLogout} sx={{ py: 1.2, fontSize: '0.85rem', color: '#ef4444' }}>
            <ListItemIcon><LogoutIcon sx={{ color: '#ef4444' }} /></ListItemIcon>
            Log Out
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  )

  return (
    <>
      <AppBar position="fixed" sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        display: { sm: 'none' },
        bgcolor: '#0f172a'
      }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <SignOutAllDevicesDialog open={signOutAll} onClose={() => setSignOutAll(false)} onSignedOut={handleLogout} />

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '75%', bgcolor: '#0f172a', border: 'none' }
          }}>
          {DrawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box', width: drawerWidth,
              bgcolor: '#0f172a', border: 'none',
              borderRight: '1px solid rgba(255,255,255,0.06)'
            }
          }}
          open>
          {DrawerContent}
        </Drawer>
      </Box>
    </>
  )
}

export default MerchantSidebar
